import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type Contact, type ReminderRow, type TimelineEntry } from "../lib/api";

type FieldDef = { key: string; label: string };

/** Every field, grouped the way someone thinks about a person rather than the
 *  way the table is laid out. A field that exists but is never shown is worse
 *  than one that does not exist - people conclude we never captured it. */
const GROUPS: Array<{ title: string; fields: Array<[string, string]> }> = [
  {
    title: "Who they are",
    fields: [
      ["phone_e164", "Phone"],
      ["email", "Email"],
      ["city", "City"],
      ["state", "State"],
      ["postal_code", "Postcode"],
      ["timezone", "Timezone"],
      ["source", "Came from"],
      ["signup_at", "Signed up"],
      ["external_id", "Id in your other system"],
      ["five9_contact_id", "Five9 id"],
      ["created_at", "Added"],
      ["updated_at", "Last changed"],
    ],
  },
  {
    title: "Can we message them",
    fields: [
      ["consent_status", "Consent"],
      ["consent_source", "Consent came from"],
      ["consent_at", "Consent recorded"],
      ["opted_out_at", "Opted out"],
      ["dnc", "Do not contact"],
      ["phone_valid", "Number works"],
      ["hard_fail_count", "Delivery failures"],
      ["subscriber_status", "Subscriber status"],
      ["automation_paused_until", "Automation paused until"],
      ["auto_dnd_at", "Auto-DND date"],
    ],
  },
  {
    title: "Messaging",
    fields: [
      ["responsiveness", "Responsiveness"],
      ["first_reply_at", "First ever reply"],
      ["last_reply_at", "Last replied"],
      ["last_outbound_at", "Last messaged"],
      ["reply_count", "Replies"],
      // Counted from their messages below, not read from the columns of the
      // same name. sends_30d is not shown at all: nothing keeps it true and no
      // cap uses it.
      ["sends_today", "Sent today"],
      ["sends_7d", "Sent in the last 7 days"],
      ["sends_total", "Sent ever"],
      ["best_send_hour_local", "Best hour to send"],
    ],
  },
  {
    title: "Calls",
    fields: [
      ["calls_total", "Calls made"],
      ["calls_connected", "Calls connected"],
      ["last_call_at", "Last call"],
      ["last_call_disposition", "How it went"],
      ["last_closer_user_id", "Last closer"],
    ],
  },
  {
    title: "Offers and sales",
    fields: [
      ["last_offer", "Last offer"],
      ["last_offer_at", "Offered on"],
      ["sale_count", "Sales"],
      ["lifetime_value_cents", "Lifetime value"],
    ],
  },
  {
    title: "Appointments",
    fields: [
      ["appointment_count", "Booked"],
      ["appointments_kept", "Kept"],
      ["appointments_no_show", "No-showed"],
      ["next_appointment_at", "Next one"],
    ],
  },
  {
    title: "Travel",
    fields: [
      ["flight_count", "Trips taken"],
      ["last_flight_at", "Last flew"],
    ],
  },
  {
    title: "Scoring and campaigns",
    fields: [
      ["lead_score", "Lead score"],
      ["lead_score_band", "Band"],
      ["first_reply_campaign_id", "First replied to campaign"],
      ["first_reply_day_offset", "On day"],
      ["experiment_bucket", "A/B bucket"],
      ["owner_user_id", "Owner"],
      ["team", "Team"],
    ],
  },
];

const DATE_FIELDS = new Set([
  "created_at", "updated_at", "consent_at", "opted_out_at", "first_reply_at",
  "last_reply_at", "last_outbound_at", "last_call_at", "last_offer_at",
  "next_appointment_at", "automation_paused_until", "last_flight_at",
  "signup_at", "auto_dnd_at",
]);

/** Statuses the engine counts as "we sent this person a message"
 *  (app/engine/eligibility.py COUNTS_AS_SENT). A failed or held-back message
 *  did not reach them and does not spend their allowance. */
const COUNTS_AS_SENT = new Set(["sending", "sent", "delivered", "unknown"]);

/** Midnight in `zone`, as a UTC instant. The daily cap is per calendar day in
 *  the send window's zone, so "today" has to mean the same day the engine
 *  means, not the day the person reading the screen happens to be in. */
function startOfDay(zone: string): number {
  const now = new Date();
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", { timeZone: zone })
    .format(now)
    .split("-")
    .map(Number);
  const naive = Date.UTC(y, m - 1, d, 0, 0);
  const probe = new Date(naive);
  const inZone = new Date(probe.toLocaleString("en-US", { timeZone: zone }));
  const inUtc = new Date(probe.toLocaleString("en-US", { timeZone: "UTC" }));
  return naive + (inUtc.getTime() - inZone.getTime());
}

/** How many messages actually went to this person today and in the last seven
 *  days, counted off their own timeline.
 *
 *  The contact table carries sends_today, sends_7d and sends_30d, and nothing
 *  in the system has ever reset them: they are incremented on every send, so
 *  all three read the lifetime total and a person messaged 23 times over
 *  twelve days shows "Sent today 23". The engine stopped trusting them for the
 *  caps for exactly that reason (eligibility.sends_since counts the messages);
 *  this screen counts them the same way, so the two agree.
 */
function countSends(entries: TimelineEntry[]): Record<string, string> {
  const now = Date.now();
  const midnight = startOfDay("America/New_York");
  const weekAgo = now - 7 * 86_400_000;

  const sends = entries.filter(
    (e) => e.kind === "outbound" && COUNTS_AS_SENT.has(String(e.meta?.status ?? "")),
  );
  const at = (e: TimelineEntry) => new Date(e.at).getTime();
  // Both ends. A window with no far end counts a message dated next Tuesday as
  // one sent today, which is how staging's forward-dated rows made a contact
  // last messaged on the 19th read "sent today 23".
  const between = (from: number, to: number) =>
    sends.filter((e) => at(e) >= from && at(e) < to).length;

  // The timeline returns the newest 100 messages. Under the caps (3 a day, 17
  // a week) that cannot truncate either window - but if it ever did, say so
  // rather than report a floor as a total.
  const fromMessages = entries.filter((e) =>
    ["outbound", "inbound", "suppressed"].includes(e.kind),
  );
  const oldest = fromMessages.length
    ? Math.min(...fromMessages.map(at))
    : Number.NEGATIVE_INFINITY;
  const partial = (from: number) => fromMessages.length >= 100 && oldest > from;

  return {
    sends_today: `${between(midnight, midnight + 86_400_000)}${
      partial(midnight) ? "+" : ""
    }`,
    sends_7d: `${between(weekAgo, now)}${partial(weekAgo) ? "+" : ""}`,
  };
}

function show(key: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (key === "lifetime_value_cents") return `$${(Number(value) / 100).toFixed(2)}`;
  if (key === "best_send_hour_local") return `${String(value).padStart(2, "0")}:00`;
  if (DATE_FIELDS.has(key)) return fmt.when(String(value));
  if (typeof value === "string") return value.replace(/_/g, " ");
  return String(value);
}

export function ContactDetail() {
  const { id } = useParams();
  const [contact, setContact] = useState<Contact | null>(null);
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [defs, setDefs] = useState<FieldDef[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [reminders, setReminders] = useState<ReminderRow[]>([]);

  useEffect(() => {
    if (!id) return;
    Promise.all([api.contact(Number(id)), api.timeline(Number(id))])
      .then(([c, t]) => {
        setContact(c);
        setEntries(t);
      })
      .catch((e) => setError(e.message));
    // so a custom field shows its proper name rather than its storage key
    api.customFields().then(setDefs).catch(() => setDefs([]));
    // a missing panel is better than a broken page, so a failure here is quiet
    api.contactReminders(Number(id)).then((r) => setReminders(r.items)).catch(() => setReminders([]));
  }, [id]);

  if (error) return <ErrorBanner error={error} />;
  if (!contact) return <div className="empty">Loading…</div>;

  const record = contact as unknown as Record<string, unknown>;
  const counted = countSends(entries);
  const valueFor = (k: string) => (k in counted ? counted[k] : record[k]);
  const attrs = contact.attrs ?? {};
  const labelFor = (k: string) => defs.find((d) => d.key === k)?.label ?? k.replace(/_/g, " ");

  return (
    <div className="sidebar">
      <div className="col">
        <Card
          title="Everything we know"
          actions={
            <label className="row dim" style={{ gap: 6, fontSize: 11.5, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showEmpty}
                onChange={(e) => setShowEmpty(e.target.checked)}
              />
              show empty fields
            </label>
          }
          footer={
            <span className="dim">
              Empty fields are hidden by default — a screen of dashes hides the few
              things that are actually filled in. "Sent today" and "Sent in the last
              7 days" are counted from their messages, the same way the frequency
              caps count them.
            </span>
          }
        >
          {GROUPS.map((g) => {
            const rows = g.fields.filter(([k]) => {
              const v = valueFor(k);
              return showEmpty || (v !== null && v !== undefined && v !== "");
            });
            if (rows.length === 0) return null;
            return (
              <div key={g.title} style={{ marginBottom: 16 }}>
                <span className="lab">{g.title}</span>
                <div style={{ marginTop: 4 }}>
                  {rows.map(([k, label]) => (
                    <div className="brk" key={k}>
                      <span className="dim">{label}</span>
                      <span className="mono" style={{ fontSize: 12 }}>
                        {show(k, valueFor(k))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div>
            <span className="lab">
              Their own fields · {Object.keys(attrs).length}
            </span>
            {Object.keys(attrs).length === 0 ? (
              <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
                Nothing beyond the standard fields. Columns we don't recognise on an
                import land here automatically.
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                {Object.entries(attrs).map(([k, v]) => (
                  <div className="brk" key={k}>
                    <span className="dim">{labelFor(k)}</span>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {String(v)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {reminders.length > 0 && (
          <Card
            title="Appointment reminders"
            footer={
              <span className="dim">
                Eastern times. Moving or cancelling the appointment cancels these and, for a
                move, schedules a new set.
              </span>
            }
          >
            <div className="tw">
              <table>
                <thead>
                  <tr><th>Appointment</th><th>Reminder</th><th>Goes out</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {reminders.map((r) => (
                    <tr key={r.id} style={{ opacity: r.state === "cancelled" ? 0.55 : 1 }}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {fmt.et(r.appointment_at)}
                        <div className="dim" style={{ fontSize: 11 }}>{r.calendar ?? ""}</div>
                      </td>
                      <td>{r.kind_label}</td>
                      <td className="mono" style={{ whiteSpace: "nowrap" }}>{fmt.et(r.send_at)}</td>
                      <td>
                        <Pill tone={r.state === "sent" ? "ok" : r.state === "overdue" ? "ba" : r.state === "due" ? "wa" : "nu"}>
                          {r.state === "due" ? "to go" : r.state}
                        </Pill>
                        {r.why_cancelled && (
                          <div className="dim" style={{ fontSize: 11 }}>{r.why_cancelled.replace(/_/g, " ")}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card
          title="Everything that happened"
          footer={
            <span className="dim">
              Sends, replies, campaign moves and anything held back — in one place.
            </span>
          }
        >
          {entries.length === 0 ? (
            <div className="empty">Nothing yet for this contact.</div>
          ) : (
            entries.map((e, i) => (
              <div className="tl-item" key={i}>
                <div className="tl-time">{fmt.when(e.at)}</div>
                <div>
                  <div className="row w" style={{ gap: 7 }}>
                    <b style={{ fontSize: 12 }}>{e.title}</b>
                    {e.detail && (
                      <Pill
                        tone={
                          e.kind === "inbound" ? "ac" : e.kind === "suppressed" ? "wa" : "nu"
                        }
                      >
                        {e.detail}
                      </Pill>
                    )}
                    {/* Whether this text was written for this person or was
                        the template. Reading the thread without it, the two
                        are indistinguishable - and the difference is the
                        whole point of a customised blast. */}
                    {e.meta?.customized === true && <Pill tone="ok">written for them</Pill>}
                    {e.meta?.customized === false && (
                      <Pill tone="nu">template</Pill>
                    )}
                  </div>
                  {e.body && (
                    <div className={`bub ${e.kind === "inbound" ? "in" : ""}`}>{e.body}</div>
                  )}
                  {Boolean(
                    e.meta?.day_offset != null ||
                      e.meta?.angle ||
                      e.meta?.fell_back_because,
                  ) && (
                    <div className="dim mono" style={{ fontSize: 10, marginTop: 4 }}>
                      {e.meta?.day_offset != null && (
                        <>day {String(e.meta.day_offset)} · step {String(e.meta.step_index)}</>
                      )}
                      {e.meta?.angle != null && (
                        <>
                          {e.meta?.day_offset != null ? " · " : ""}
                          angle: {String(e.meta.angle)}
                        </>
                      )}
                      {/* Why a customised blast sent this person the template
                          anyway. Every one of these is a guard that fired, and
                          they are the only record of it. */}
                      {Array.isArray(e.meta?.fell_back_because) &&
                        e.meta.fell_back_because.length > 0 && (
                          <>
                            {" · "}
                            fell back: {String(e.meta.fell_back_because[0])}
                          </>
                        )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </Card>
      </div>

      <div className="col">
        <Card title={fmt.name(contact)}>
          <div className="col" style={{ gap: 10 }}>
            <div className="row w" style={{ gap: 5 }}>
              <Pill tone={contact.consent_status === "opted_in" ? "ok" : "ba"}>
                {contact.consent_status.replace("_", " ")}
              </Pill>
              <Pill tone="nu">{contact.responsiveness.replace(/_/g, " ")}</Pill>
              {contact.lead_score_band && (
                <Pill tone="ac">band {contact.lead_score_band}</Pill>
              )}
            </div>

            <div className="tiles">
              <div className="tile">
                <span className="lab">Replies</span>
                <span className="v">{fmt.n(contact.reply_count ?? 0)}</span>
              </div>
              <div className="tile">
                <span className="lab">Calls connected</span>
                <span className="v">{fmt.n(contact.calls_connected ?? 0)}</span>
              </div>
              <div className="tile">
                <span className="lab">Trips</span>
                <span className="v">{fmt.n(contact.flight_count)}</span>
              </div>
              <div className="tile ok">
                <span className="lab">Sales</span>
                <span className="v">{fmt.n(contact.sale_count ?? 0)}</span>
              </div>
            </div>

            <div className="dim" style={{ fontSize: 11.5 }}>
              {contact.timezone} · inferred from {contact.timezone_source ?? "default"}
            </div>

            {contact.consent_status === "opted_out" || contact.dnc ? (
              <div className="banner bad stack">
                <b>Blocked.</b> Nothing will send to this person, from any campaign or
                blast. This cannot be undone from here — by design.
              </div>
            ) : blocking ? (
              <div className="banner warn stack">
                <b>Stop all messages to this person?</b> They exit every campaign,
                anything queued for them is cancelled, and the number is blocked
                permanently. <b>This cannot be undone from the console.</b>
                <div className="row" style={{ gap: 8, marginTop: 10 }}>
                  <button
                    className="btn"
                    style={{ color: "var(--bad)", borderColor: "var(--bad)" }}
                    onClick={() =>
                      api
                        .suppressContact(contact.id)
                        .then(() => api.contact(contact.id).then(setContact))
                        .catch((e) => setError(e.message))
                        .finally(() => setBlocking(false))
                    }
                  >
                    Yes, block them
                  </button>
                  <button className="btn s" onClick={() => setBlocking(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn s"
                style={{ color: "var(--bad)", borderColor: "var(--line)" }}
                onClick={() => setBlocking(true)}
              >
                They asked to stop
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
